/**
 * Field Trip area geometry: the epack row stores both a box (ul/br) and a
 * midpoint (mid_lat/mid_lon). Those can diverge after the box is moved — the
 * hotspot editor drew the box from ul/br but dropped new hotspots at mid, which
 * then looked like they landed in the previous Field Trip area.
 */

export function localeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function parseLocaleBounds(locale) {
  if (!locale) {
    return null;
  }
  const ul_lat = localeNumber(locale.ul_lat);
  const ul_lon = localeNumber(locale.ul_lon);
  const br_lat = localeNumber(locale.br_lat);
  const br_lon = localeNumber(locale.br_lon);
  if (ul_lat == null || ul_lon == null || br_lat == null || br_lon == null) {
    return null;
  }
  return { ul_lat, ul_lon, br_lat, br_lon };
}

export function localeBoxCenter(bounds) {
  if (!bounds) {
    return null;
  }
  return {
    lat: (bounds.ul_lat + bounds.br_lat) / 2,
    lng: (bounds.ul_lon + bounds.br_lon) / 2,
  };
}

export function isCoordInLocaleBounds(lat, lon, bounds) {
  if (!bounds) {
    return false;
  }
  const latN = localeNumber(lat);
  const lonN = localeNumber(lon);
  if (latN == null || lonN == null) {
    return false;
  }
  const minLat = Math.min(bounds.ul_lat, bounds.br_lat);
  const maxLat = Math.max(bounds.ul_lat, bounds.br_lat);
  const minLon = Math.min(bounds.ul_lon, bounds.br_lon);
  const maxLon = Math.max(bounds.ul_lon, bounds.br_lon);
  return latN >= minLat && latN <= maxLat && lonN >= minLon && lonN <= maxLon;
}

/**
 * Where a newly created hotspot should land.
 *
 * Prefer the stored midpoint when it still sits inside the Field Trip box;
 * otherwise use the box centre. After the area is moved, mid_lat/mid_lon can
 * lag behind ul/br (or the hotspot page can be holding a stale copy of mid).
 */
export function defaultHotspotPosition(locale) {
  const bounds = parseLocaleBounds(locale);
  if (!bounds) {
    return null;
  }
  const midLat = localeNumber(locale.mid_lat);
  const midLon = localeNumber(locale.mid_lon);
  if (midLat != null && midLon != null && isCoordInLocaleBounds(midLat, midLon, bounds)) {
    return { lat: midLat, lng: midLon };
  }
  return localeBoxCenter(bounds);
}

export function clampToLocaleBounds(lat, lon, bounds) {
  if (!bounds) {
    return { lat, lon };
  }
  const minLat = Math.min(bounds.br_lat, bounds.ul_lat);
  const maxLat = Math.max(bounds.br_lat, bounds.ul_lat);
  const minLon = Math.min(bounds.ul_lon, bounds.br_lon);
  const maxLon = Math.max(bounds.ul_lon, bounds.br_lon);
  return {
    lat: Math.max(minLat, Math.min(lat, maxLat)),
    lon: Math.max(minLon, Math.min(lon, maxLon)),
  };
}

/**
 * Read the Field Trip box the user is looking at (GeoJSON), not the derived
 * localeCenter/localeUpperLeft copies that can lag a live drag.
 */
export function geometryFromBoxData(boxData) {
  const coords = boxData && boxData.features && boxData.features[0]
    && boxData.features[0].geometry && boxData.features[0].geometry.coordinates
    && boxData.features[0].geometry.coordinates[0];
  if (!Array.isArray(coords) || coords.length < 4) {
    return null;
  }

  const lons = coords.map((c) => c[0]).filter((n) => Number.isFinite(n));
  const lats = coords.map((c) => c[1]).filter((n) => Number.isFinite(n));
  if (lons.length < 4 || lats.length < 4) {
    return null;
  }

  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const upperLeft = { lat: maxLat, lng: minLon };
  const bottomRight = { lat: minLat, lng: maxLon };
  const center = {
    lat: (maxLat + minLat) / 2,
    lng: (minLon + maxLon) / 2,
  };
  return { upperLeft, bottomRight, center };
}

export function localeGeometryChanged(prevLocale, nextLocale) {
  if (prevLocale === nextLocale) {
    return false;
  }
  if (!prevLocale || !nextLocale) {
    return true;
  }
  if (String(prevLocale.id) !== String(nextLocale.id)) {
    return true;
  }
  return ['mid_lat', 'mid_lon', 'ul_lat', 'ul_lon', 'br_lat', 'br_lon']
    .some((key) => localeNumber(prevLocale[key]) !== localeNumber(nextLocale[key]));
}
